import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { money } from '@/lib/helpers';
import { PackageSearch, Loader2, CheckCircle2, Info } from 'lucide-react';

export default function NeedsReview() {
  const { stores } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const storeName = (id) => (stores || []).find((s) => s.id === id)?.name || id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/garments', { params: { needs_review: 'true', limit: 500 } });
      setRows((r.data.data || []).map((g) => ({ ...g, _product: g.product || '', _category: g.category || '', _cost: g.cost ?? 0 })));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const edit = (bc, patch) => setRows((rs) => rs.map((r) => (r.barcode === bc ? { ...r, ...patch } : r)));

  const saveRow = useCallback(async (r) => {
    setBusy((b) => ({ ...b, [r.barcode]: true }));
    try {
      await api.patch('/garments/bulk', {
        barcodes: [r.barcode],
        updates: { product: r._product, category: r._category, cost: r._cost },
        clear_needs_review: true,
      });
      setRows((rs) => rs.filter((x) => x.barcode !== r.barcode));
      toast.success(`${r.barcode} reviewed`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy((b) => ({ ...b, [r.barcode]: false })); }
  }, []);

  return (
    <Page>
      <TopBar title="Needs Inventory Review" />
      <div className="px-4 pt-4 pb-32 max-w-3xl mx-auto">
        <p className="mb-3 flex items-start gap-1.5 text-[12px] text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Placeholder items created from WhatsApp sales. Correct the details and mark reviewed. This does not change any invoice already issued.
        </p>
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground" data-testid="needs-review-empty">
            <PackageSearch className="mx-auto mb-2 h-8 w-8 opacity-50" />
            Nothing needs review. 🎉
          </div>
        ) : (
          <div className="space-y-2" data-testid="needs-review-list">
            {rows.map((r) => (
              <div key={r.barcode} className="rounded-xl border border-border bg-card p-3" data-testid={`needs-review-row-${r.barcode}`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="font-mono font-semibold">{r.barcode}</span>
                  <Badge variant="outline" className="bg-sky-500/15 text-sky-600 border-sky-500/30">{storeName(r.store_id)}</Badge>
                  <span className="ml-auto text-[11px] text-muted-foreground">sold price {money(r.price)}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input value={r._product} onChange={(e) => edit(r.barcode, { _product: e.target.value })}
                    placeholder="Product name" data-testid={`needs-review-product-${r.barcode}`} className="h-9 rounded-lg text-sm" />
                  <Input value={r._category} onChange={(e) => edit(r.barcode, { _category: e.target.value })}
                    placeholder="Category" data-testid={`needs-review-category-${r.barcode}`} className="h-9 rounded-lg text-sm" />
                  <Input type="number" value={r._cost} onChange={(e) => edit(r.barcode, { _cost: Number(e.target.value) })}
                    placeholder="Cost" data-testid={`needs-review-cost-${r.barcode}`} className="h-9 rounded-lg text-sm" />
                </div>
                <div className="mt-2 flex justify-end">
                  <Button size="sm" disabled={!!busy[r.barcode]} onClick={() => saveRow(r)}
                    data-testid={`needs-review-save-${r.barcode}`} className="h-9 rounded-lg">
                    {busy[r.barcode] ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                    Save & mark reviewed
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}
